import React, { useState, useEffect } from "react";
import axios from "axios";


// MUI imports
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Checkbox,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Card,
  CircularProgress,
  Backdrop,
} from "@mui/material";

import {
  Delete as DeleteIcon,
  ArrowUpward,
  ArrowDownward,
  Edit as EditIcon,
  HelpOutline as HelpIcon
} from "@mui/icons-material";

// For charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AGGREGATORS = ["None", "SUM", "COUNT", "AVG", "MIN", "MAX"];

// A small helper to parse value as number safely
function toNumber(value) {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

// Define help texts for each section
const helpTexts = {
  savedReports: `
You can view your list of saved reports in this section. 
- Click on a report name to load it into the builder on the right. 
- Use the icons to edit or delete a saved report. 
- The search box filters reports by name or category.
Example: Type "sales" in the search to find all reports that have "sales" in the name or category.
`,
  baseTable: `
The base table is the primary data source for the report. 
- Choose the table that contains the main set of records you want to query.
Example: If you want to build a report about shipments, choose the "shipments" table.
`,
  limit: `
Use "Limit" to control how many rows are returned by the query. 
- Setting this to a lower number (e.g. 100) can help you test a report quickly. 
- For a full dataset, set it higher (or remove the limit in your backend, if supported).
`,
  chartType: `
Select a chart type to visualize your results. 
- "Bar" creates a bar chart comparing different categories or values. 
- "Doughnut" creates a circular chart. 
- "(No Chart)" will hide the chart entirely.
Example: If you have grouped data (like "region") with a SUM of a numeric column (like "sales"), a bar chart can show total sales per region.
`,
  labelField: `
"Label Field" determines the text or category labels in your chart. 
Example: If you're making a bar chart to show total sales by region, the label field would be "region" or "sales.region" (depending on your naming).
`,
  valueField: `
"Value Field" is the numeric or aggregated field used to plot the height or size of the chart segments. 
Example: "SUM(sales.amount)" for total sales. 
If you see empty or zero values, ensure you have the correct aggregator selected.
`,
  joins: `
Joins let you combine data from multiple tables in one query. 
- The join type (INNER, LEFT, RIGHT) determines how rows are matched:
  • INNER: only rows with matching values in both tables.
  • LEFT: all rows from the base table, plus matching rows from the joined table.
  • RIGHT: all rows from the joined table, plus matching rows from the base table.
- "Local Field" is typically the column from your base (or previously joined) table.
- "Foreign Field" is the matching column on the table you're joining.
Example: If "shipments" has a column "po_id", and "purchase_orders" also has "po_id", you can do:
  • Join Table = purchase_orders
  • Local Field = shipments.po_id
  • Foreign Field = purchase_orders.po_id
`,
  selectFields: `
Select which columns you want to include in your report. 
- Click the checkbox next to a column name to include it. 
- You can optionally apply an aggregator like SUM, COUNT, AVG, MIN, or MAX. 
- Reorder your selected fields with the up/down arrows in the "Selected Fields" list.
Example: You might select "shipments.date_shipped" and "SUM(shipments.quantity)" to see the sum of quantities shipped by date.
`,
  filters: `
Filters narrow down your results by only including rows that match certain criteria. 
- Choose a column, operator (=, >, <, etc.), and a value. 
- You can add multiple filters to refine your data.
Example: If you want shipments from 2021 only, set:
  • Field = shipments.date_shipped 
  • Operator = >= 
  • Value = 2021-01-01
  Then add another filter for <= 2021-12-31 if desired.
`,
  groupBy: `
"Group By" combines rows that share identical values in specified columns. 
- Typically used along with aggregator functions (e.g. SUM, COUNT) to produce summary rows per group.
Example: If you want total quantity per product, you'd select the "product" column as a group-by, and choose "SUM" aggregator on the "quantity" column.
`,
  sort: `
"Sort" lets you define how the results are ordered. 
- You can add multiple sort criteria, specifying ascending (ASC) or descending (DESC).
Example: Sort by "date_shipped" ASC, then by "customer_name" ASC to see the most recent shipments first, grouped by customer.
`,
  runReport: `
"Run Report" executes the query based on your current selections (fields, joins, filters, grouping, and sorting). 
- The result rows are displayed in the Results section below.
`,
  exportCsv: `
"Export CSV" downloads your current results in a CSV file so you can open them in Excel or another tool.
`,
  saveReport: `
"Save Report" creates a new saved report using the current configuration (tables, fields, filters, etc.). 
- You'll be prompted for a name, category, and "created by" identifier.
`,
};

function ReportsModule() {
  // -------------------------------------------------------
  // 1) State: Left Panel - Saved Reports
  // -------------------------------------------------------
  const [savedReports, setSavedReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal for deleting a saved report
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // -------------------------------------------------------
  // 2) State: Right Panel - Building a New or Editing a Saved Report
  // -------------------------------------------------------
  const [allTables, setAllTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");

  // metadataMap holds { [tableName]: [ {name, type, is_nullable}, ... ] }
  // so we can track columns for base table + each joined table
  const [metadataMap, setMetadataMap] = useState({});

  // Joins array: { joinType, table, localField, foreignField }
  const [joins, setJoins] = useState([]);

  // fieldConfigs: array of { column: "table.col", aggregator: "None"/"SUM"/"COUNT"/... }
  const [fieldConfigs, setFieldConfigs] = useState([]);

  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [sort, setSort] = useState([]);
  const [limit, setLimit] = useState(1000);

  // Chart
  const [showChart, setShowChart] = useState(false);
  const [chartType, setChartType] = useState("bar"); // "bar" or "doughnut"
  const [chartLabelField, setChartLabelField] = useState("");
  const [chartValueField, setChartValueField] = useState("");

  // Query Results
  const [resultColumns, setResultColumns] = useState([]);
  const [resultRows, setResultRows] = useState([]);

  // Save Report Modal (for creating new report)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportCategory, setReportCategory] = useState("");
  const [reportCreatedBy, setReportCreatedBy] = useState("");

  // Edit Report Modal (for updating an existing report)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [reportToEdit, setReportToEdit] = useState(null);
  const [reportNameEdit, setReportNameEdit] = useState("");
  const [reportCategoryEdit, setReportCategoryEdit] = useState("");
  const [reportCreatedByEdit, setReportCreatedByEdit] = useState("");

  // -------------------------------------------------------
  // 3) Help Dialog State
  // -------------------------------------------------------
  const [helpTopic, setHelpTopic] = useState(null); // which section's help is open?

  // -------------------------------------------------------
  // 4) Loading state for presets
  // -------------------------------------------------------
  const [loadingPreset, setLoadingPreset] = useState(false);

  // Simple handlers to open/close help dialog
  const handleOpenHelp = (topic) => {
    setHelpTopic(topic);
  };
  const handleCloseHelp = () => {
    setHelpTopic(null);
  };

  /**
   * Processes the filters array before sending it to the backend.
   * Specifically, it converts string values for 'between' operators
   * into an array of two strings.
   */
  const processFiltersForBackend = (filtersToProcess) => {
    if (!Array.isArray(filtersToProcess)) {
      return [];
    }
    
    return filtersToProcess.map(filter => {
      // Check if the operator is 'between' and the value is a string
      if (filter.operator === 'between' && typeof filter.value === 'string') {
        // Split the string by comma, and trim whitespace from each part.
        const values = filter.value.split(',').map(v => v.trim());
        
        // Return the new filter object with the value as an array
        return { ...filter, value: values };
      }
      // Return all other filters unchanged
      return filter;
    });
  };

  // -------------------------------------------------------
  // Effects: fetch saved reports & tables on mount
  // -------------------------------------------------------
  useEffect(() => {
    fetchSavedReports();
    fetchAllTables();
  }, []);

  // -------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------
  const fetchSavedReports = async () => {
    try {
      const res = await axios.get("/reports/saved");
      setSavedReports(res.data || []);
    } catch (err) {
      console.error("Error fetching saved reports:", err);
      alert("Failed to fetch saved reports.");
    }
  };

  const fetchAllTables = async () => {
    try {
      const res = await axios.get("/reports/tables");
      setAllTables(res.data.tables || []);
    } catch (err) {
      console.error("Error fetching tables:", err);
      alert("Failed to fetch table list.");
    }
  };

  // Always fetch fresh columns for each table
  const fetchMetadataForTable = async (tableName) => {
    if (!tableName) return;
    try {
      const res = await axios.get("/reports/metadata", {
        params: { table: tableName },
      });
      setMetadataMap(prevMap => ({
        ...prevMap,
        [tableName]: res.data.columns || []
      }));
    } catch (err) {
      console.error("Error fetching table metadata:", err);
      alert("Failed to fetch table metadata for " + tableName);
    }
  };

  // -------------------------------------------------------
  // 5) Handlers: Left panel interactions
  // -------------------------------------------------------
  const handleSelectReport = async (r) => {
    setLoadingPreset(true);
    try {
      const c = r.config || {};

      // Identify all tables used
      let tableNames = [];
      if (c.baseTable) {
        tableNames.push(c.baseTable);
      }
      if (Array.isArray(c.joins)) {
        c.joins.forEach((jn) => {
          if (jn.table) {
            tableNames.push(jn.table);
          }
        });
      }
      tableNames = [...new Set(tableNames)];

      // Fetch all needed metadata in parallel
      await Promise.all(tableNames.map((tbl) => fetchMetadataForTable(tbl)));

      // Update local states after metadata is loaded
      setSelectedReportId(r.report_id);
      setSelectedTable(c.baseTable || "");
      setJoins(c.joins || []);
      setFilters(c.filters || []);
      setGroupBy(c.groupBy || []);
      setSort(c.sort || []);
      setLimit(c.limit || 1000);

      // Add this block to load the chart configuration
      const savedChartType = c.chartType || 'none';
      setShowChart(savedChartType !== 'none');
      setChartType(savedChartType === 'none' ? 'bar' : savedChartType); // Default to 'bar' if not 'none'
      setChartLabelField(c.chartLabelField || "");
      setChartValueField(c.chartValueField || "");

      // More robust aggregator detection
      const aggregatorRegex = /^(SUM|COUNT|AVG|MIN|MAX)\s*\(\s*(.*?)\s*\)\s+AS\s+(.*)$/i;

      // Build fieldConfigs from raw saved fields array
      const loadedFieldConfigs = (c.fields || []).map((f) => {
        // Attempt aggregator parse
        const match = aggregatorRegex.exec(f);
        if (match) {
          return {
            aggregator: match[1].toUpperCase(),
            column: match[2].trim(),
          };
        }
        // Otherwise, assume no aggregator
        return { column: f, aggregator: "None" };
      });
      setFieldConfigs(loadedFieldConfigs);

      // Clear any previous results
      setResultColumns([]);
      setResultRows([]);
    } catch (err) {
      console.error("Error loading saved report:", err);
      alert("Failed to load saved report details");
    } finally {
      setLoadingPreset(false);
    }
  };

  const handleDeleteSavedReport = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSavedReport = async () => {
    if (!reportToDelete) {
      setDeleteDialogOpen(false);
      return;
    }
    try {
      await axios.delete(`/reports/saved/${reportToDelete.report_id}`);
      alert("Report deleted.");
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchSavedReports();
    } catch (err) {
      console.error("Error deleting report:", err);
      alert("Failed to delete saved report");
    }
  };

  const handleEditSavedReport = (report) => {
    setReportToEdit(report);
    setReportNameEdit(report.report_name || "");
    setReportCategoryEdit(report.category || "");
    setReportCreatedByEdit(report.created_by || "");
    setEditDialogOpen(true);
  };

  const handleUpdateSavedReport = async () => {
    if (!reportToEdit) return;
    const finalFields = buildFinalFieldsArray();
    const processedFilters = processFiltersForBackend(filters); // <-- FIX HERE

    const config = {
      baseTable: selectedTable,
      joins,
      fields: finalFields,
      filters: processedFilters, // <-- USE PROCESSED FILTERS
      groupBy,
      sort,
      limit: toNumber(limit),
      // Add the chart state here
      chartType: showChart ? chartType : 'none',
      chartLabelField,
      chartValueField,
    };

    const payload = {
      report_name: reportNameEdit,
      category: reportCategoryEdit,
      created_by: reportCreatedByEdit,
      config,
    };

    try {
      await axios.put(`/reports/saved/${reportToEdit.report_id}`, payload);
      alert("Report updated successfully!");
      setEditDialogOpen(false);
      setReportToEdit(null);
      fetchSavedReports();
    } catch (err) {
      console.error("Error updating report:", err);
      alert("Failed to update report");
    }
  };

  // -------------------------------------------------------
  // 6) Handlers: Right panel (report building)
  // -------------------------------------------------------
  const handleTableChange = async (tableName) => {
    // Reset state when changing base table
    setSelectedReportId(null);
    setSelectedTable(tableName);
    setJoins([]);
    setFieldConfigs([]);
    setFilters([]);
    setGroupBy([]);
    setSort([]);
    setLimit(1000);
    setResultColumns([]);
    setResultRows([]);

    if (tableName) {
      await fetchMetadataForTable(tableName);
    }
  };

  const addJoin = () => {
    setJoins((prev) => [
      ...prev,
      { joinType: "INNER", table: "", localField: "", foreignField: "" },
    ]);
  };

  const handleJoinTableChange = async (idx, newTable) => {
    const updated = [...joins];
    updated[idx].table = newTable;
    setJoins(updated);
    if (newTable) {
      await fetchMetadataForTable(newTable);
    }
  };

  const removeJoin = (idx) => {
    const updated = [...joins];
    updated.splice(idx, 1);
    setJoins(updated);
  };

  const addFilterRow = () => {
    setFilters((prev) => [...prev, { field: "", operator: "=", value: "" }]);
  };

  const removeFilterRow = (index) => {
    const updated = [...filters];
    updated.splice(index, 1);
    setFilters(updated);
  };

  // Combined columns from base + joined tables
  const allActiveTables = [
    selectedTable,
    ...joins.map((jn) => jn.table).filter(Boolean),
  ];

  let combinedColumns = [];
  allActiveTables.forEach((tbl) => {
    if (tbl && metadataMap[tbl]) {
      metadataMap[tbl].forEach((col) => {
        combinedColumns.push({ tableName: tbl, colName: col.name });
      });
    }
  });
  const combinedColumnNames = combinedColumns.map(
    (cc) => `${cc.tableName}.${cc.colName}`
  );

  // Render aggregator selection
  const aggregatorSelect = (fullName, currentAgg) => (
    <FormControl size="small" sx={{ minWidth: 80, mr: 1 }}>
      <InputLabel>Agg</InputLabel>
      <Select
        label="Agg"
        value={currentAgg}
        onChange={(e) => setAggregatorForColumn(fullName, e.target.value)}
      >
        {AGGREGATORS.map((aggOpt) => (
          <MenuItem key={aggOpt} value={aggOpt}>
            {aggOpt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const isColumnChecked = (fullName) => {
    return fieldConfigs.some((fc) => fc.column === fullName);
  };

  const toggleColumnSelected = (fullName, isSelected) => {
    if (isSelected) {
      setFieldConfigs((prev) => [...prev, { column: fullName, aggregator: "None" }]);
    } else {
      setFieldConfigs((prev) => prev.filter((fc) => fc.column !== fullName));
    }
  };

  const setAggregatorForColumn = (fullName, aggregator) => {
    setFieldConfigs((prev) =>
      prev.map((fc) => {
        if (fc.column === fullName) {
          return { ...fc, aggregator };
        }
        return fc;
      })
    );
  };

  // Reordering
  const moveFieldUp = (index) => {
    if (index === 0) return;
    const newConfigs = [...fieldConfigs];
    [newConfigs[index - 1], newConfigs[index]] = [
      newConfigs[index],
      newConfigs[index - 1],
    ];
    setFieldConfigs(newConfigs);
  };

  const moveFieldDown = (index) => {
    if (index === fieldConfigs.length - 1) return;
    const newConfigs = [...fieldConfigs];
    [newConfigs[index], newConfigs[index + 1]] = [
      newConfigs[index + 1],
      newConfigs[index],
    ];
    setFieldConfigs(newConfigs);
  };

  // Build final SQL-ready fields array
  const buildFinalFieldsArray = () => {
    return fieldConfigs.map((fc) => {
      const col = fc.column;
      const agg = fc.aggregator;
      if (!agg || agg === "None") {
        return col;
      }
      const aliasPart = col.replace(/\./g, "_");
      const alias = `${agg.toLowerCase()}_${aliasPart}`;
      return `${agg}(${col}) AS ${alias}`;
    });
  };

  const handleRunReport = async () => {
    if (!selectedTable) {
      alert("Please select a base table first.");
      return;
    }
    if (fieldConfigs.length === 0) {
      alert("Please select at least one field.");
      return;
    }

    try {
      const finalFields = buildFinalFieldsArray();
      const processedFilters = processFiltersForBackend(filters); // <-- FIX HERE

      const payload = {
        baseTable: selectedTable,
        joins,
        fields: finalFields,
        filters: processedFilters, // <-- USE PROCESSED FILTERS
        groupBy,
        sort,
        limit: toNumber(limit),
      };
      const res = await axios.post("/reports/query", payload);
      if (res.data && res.data.rows) {
        setResultColumns(res.data.columns || []);
        setResultRows(res.data.rows);
      } else {
        setResultColumns([]);
        setResultRows([]);
      }
    } catch (err) {
      console.error("Error running report:", err);
      alert("Failed to run report");
    }
  };

  const handleExportCsv = async () => {
    if (!selectedTable || fieldConfigs.length === 0) {
      alert("No table or fields selected.");
      return;
    }

    try {
      const finalFields = buildFinalFieldsArray();
      const processedFilters = processFiltersForBackend(filters); // <-- FIX HERE

      const payload = {
        baseTable: selectedTable,
        joins,
        fields: finalFields,
        filters: processedFilters, // <-- USE PROCESSED FILTERS
        groupBy,
        sort,
        limit: toNumber(limit),
      };
      const response = await axios.post("/reports/query?export=true", payload, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "report.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      alert("Failed to export CSV");
    }
  };

  // Save new report
  const handleOpenSaveDialog = () => {
    setReportName("");
    setReportCategory("");
    setReportCreatedBy("");
    setSaveDialogOpen(true);
  };

  const handleCloseSaveDialog = () => {
    setSaveDialogOpen(false);
  };

  const handleSaveReport = async () => {
    if (!selectedTable || fieldConfigs.length === 0) {
      alert("Please select a base table and at least one field before saving.");
      return;
    }

    const finalFields = buildFinalFieldsArray();
    const processedFilters = processFiltersForBackend(filters); // <-- FIX HERE

    const config = {
      baseTable: selectedTable,
      joins,
      fields: finalFields,
      filters: processedFilters, // <-- USE PROCESSED FILTERS
      groupBy,
      sort,
      limit: toNumber(limit),
      // Add the chart state here
      chartType: showChart ? chartType : 'none',
      chartLabelField,
      chartValueField,
    };
    const payload = {
      report_name: reportName,
      category: reportCategory,
      created_by: reportCreatedBy,
      config,
    };
    try {
      const res = await axios.post("/reports/save", payload);
      alert("Report saved! ID=" + res.data.report_id);
      setSaveDialogOpen(false);
      fetchSavedReports();
    } catch (err) {
      console.error("Error saving report:", err);
      alert("Failed to save report");
    }
  };

  // Chart data
  let chartData = null;
  if (resultRows.length > 0 && chartLabelField && chartValueField) {
    const labels = resultRows.map((r) => String(r[chartLabelField] || ""));
    const values = resultRows.map((r) => toNumber(r[chartValueField]));
    chartData = {
      labels,
      datasets: [
        {
          label: chartValueField,
          data: values,
          backgroundColor:
            chartType === "bar"
              ? "rgba(54, 162, 235, 0.6)"
              : [
                  "#ff6384",
                  "#36a2eb",
                  "#cc65fe",
                  "#ffce56",
                  "#4bc0c0",
                  "#9966ff",
                ],
        },
      ],
    };
  }

  // Filter saved reports by search term
  const filteredSavedReports = savedReports.filter((sr) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      sr.report_name.toLowerCase().includes(term) ||
      (sr.category && sr.category.toLowerCase().includes(term))
    );
  });

  // -------------------------------------------------------
  // 7) Render
  // -------------------------------------------------------

  // If we are loading a preset, display a quick backdrop or spinner
  return (
    <>
      <Backdrop open={loadingPreset} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: "flex", height: "100vh", backgroundColor: "#f7f7f7" }}>
        {/* Left Panel: Saved Reports */}
        <Box
          sx={{
            width: 320,
            borderRight: "1px solid #ccc",
            p: 2,
            overflowY: "auto",
            backgroundColor: "#ffffff",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" gutterBottom>
              Saved Reports
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleOpenHelp("savedReports")}
              sx={{ ml: 1 }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            label="Search"
            size="small"
            fullWidth
            sx={{ mb: 1 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <Divider sx={{ mb: 1 }} />

          <List dense>
            {filteredSavedReports.map((rep) => (
              <ListItemButton
                key={rep.report_id}
                selected={rep.report_id === selectedReportId}
                onClick={() => handleSelectReport(rep)}
                sx={{ mb: 1 }}
              >
                <ListItemText
                  primary={rep.report_name}
                  secondary={rep.category ? `Category: ${rep.category}` : ""}
                />
                <IconButton
                  color="primary"
                  size="small"
                  sx={{ mr: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditSavedReport(rep);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSavedReport(rep);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />
          <Button
            variant="contained"
            onClick={() => {
              setSelectedReportId(null);
              handleTableChange("");
            }}
            fullWidth
          >
            + New Report
          </Button>
        </Box>

        {/* Right Panel: Report Builder */}
        <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
          <Typography variant="h4" gutterBottom>
            Dynamic Report Builder
          </Typography>

          {/* Top Panel */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center">
                  <FormControl fullWidth size="small">
                    <InputLabel>Base Table</InputLabel>
                    <Select
                      label="Base Table"
                      value={selectedTable}
                      onChange={(e) => handleTableChange(e.target.value)}
                    >
                      <MenuItem value="">(Select a table)</MenuItem>
                      {allTables.map((tbl) => (
                        <MenuItem key={tbl} value={tbl}>
                          {tbl}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => handleOpenHelp("baseTable")}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>

              <Grid item xs={12} md={2}>
                <Box display="flex" alignItems="center">
                  <TextField
                    label="Limit"
                    type="number"
                    size="small"
                    fullWidth
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  />
                  <IconButton onClick={() => handleOpenHelp("limit")}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box display="flex" alignItems="center">
                  <FormControl fullWidth size="small">
                    <InputLabel>Chart Type</InputLabel>
                    <Select
                      label="Chart Type"
                      value={chartType}
                      onChange={(e) => {
                        setChartType(e.target.value);
                        setShowChart(e.target.value !== "none");
                      }}
                    >
                      <MenuItem value="none">(No Chart)</MenuItem>
                      <MenuItem value="bar">Bar</MenuItem>
                      <MenuItem value="doughnut">Doughnut</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => handleOpenHelp("chartType")}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>

              {showChart && (
                <>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center">
                      <FormControl fullWidth size="small">
                        <InputLabel>Label Field</InputLabel>
                        <Select
                          label="Label Field"
                          value={chartLabelField}
                          onChange={(e) => setChartLabelField(e.target.value)}
                        >
                          <MenuItem value="">(none)</MenuItem>
                          {fieldConfigs.map((fc) => {
                            const colName =
                              fc.aggregator === "None"
                                ? fc.column
                                : `${fc.aggregator}(${fc.column})`;
                            return (
                              <MenuItem key={colName} value={colName}>
                                {colName}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <IconButton onClick={() => handleOpenHelp("labelField")}>
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center">
                      <FormControl fullWidth size="small">
                        <InputLabel>Value Field</InputLabel>
                        <Select
                          label="Value Field"
                          value={chartValueField}
                          onChange={(e) => setChartValueField(e.target.value)}
                        >
                          <MenuItem value="">(none)</MenuItem>
                          {fieldConfigs.map((fc) => {
                            const colName =
                              fc.aggregator === "None"
                                ? fc.column
                                : `${fc.aggregator}(${fc.column})`;
                            return (
                              <MenuItem key={colName} value={colName}>
                                {colName}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <IconButton onClick={() => handleOpenHelp("valueField")}>
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Joins */}
          {selectedTable && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom sx={{ mr: 1 }}>
                  Joins
                </Typography>
                <IconButton onClick={() => handleOpenHelp("joins")} size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Box>
              {joins.map((join, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}
                >
                  <FormControl sx={{ minWidth: 100 }} size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={join.joinType}
                      onChange={(e) => {
                        const updated = [...joins];
                        updated[idx].joinType = e.target.value;
                        setJoins(updated);
                      }}
                    >
                      <MenuItem value="INNER">INNER</MenuItem>
                      <MenuItem value="LEFT">LEFT</MenuItem>
                      <MenuItem value="RIGHT">RIGHT</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 150 }} size="small">
                    <InputLabel>Join Table</InputLabel>
                    <Select
                      label="Join Table"
                      value={join.table}
                      onChange={async (e) => {
                        await handleJoinTableChange(idx, e.target.value);
                      }}
                    >
                      <MenuItem value="">(Select a table)</MenuItem>
                      {allTables.map((tbl) => (
                        <MenuItem key={tbl} value={tbl}>
                          {tbl}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Local Field"
                    size="small"
                    placeholder="e.g. shipments.po_id"
                    value={join.localField}
                    onChange={(e) => {
                      const updated = [...joins];
                      updated[idx].localField = e.target.value;
                      setJoins(updated);
                    }}
                    sx={{ width: 200 }}
                  />
                  <TextField
                    label="Foreign Field"
                    size="small"
                    placeholder="e.g. purchase_orders.po_id"
                    value={join.foreignField}
                    onChange={(e) => {
                      const updated = [...joins];
                      updated[idx].foreignField = e.target.value;
                      setJoins(updated);
                    }}
                    sx={{ width: 200 }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeJoin(idx)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button onClick={addJoin}>+ Add Join</Button>
            </Paper>
          )}

          {/* Fields Section */}
          {selectedTable && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom sx={{ mr: 1 }}>
                  Select Fields
                </Typography>
                <IconButton onClick={() => handleOpenHelp("selectFields")} size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Box>
              {allActiveTables.map((tbl) => {
                if (!tbl || !metadataMap[tbl]) return null;
                return (
                  <Box key={tbl} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">{tbl} Columns:</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                      {metadataMap[tbl].map((col) => {
                        const fullName = `${tbl}.${col.name}`;
                        const checked = isColumnChecked(fullName);
                        let currentAgg = "None";
                        const fc = fieldConfigs.find((x) => x.column === fullName);
                        if (fc) {
                          currentAgg = fc.aggregator;
                        }
                        return (
                          <Box
                            key={fullName}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 220,
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={(e) => {
                                toggleColumnSelected(fullName, e.target.checked);
                              }}
                            />
                            <Typography sx={{ mr: 1 }}>{col.name}</Typography>
                            {checked && aggregatorSelect(fullName, currentAgg)}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}

              {/* Display the selected fields in order, with Up/Down controls */}
              {fieldConfigs.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected Fields (Order)
                  </Typography>
                  {fieldConfigs.map((fc, idx) => {
                    const displayedName =
                      fc.aggregator === "None"
                        ? fc.column
                        : `${fc.aggregator}(${fc.column})`;
                    return (
                      <Box
                        key={`${displayedName}-${idx}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ flex: 1 }}>{displayedName}</Typography>
                        <IconButton
                          size="small"
                          disabled={idx === 0}
                          onClick={() => moveFieldUp(idx)}
                        >
                          <ArrowUpward fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={idx === fieldConfigs.length - 1}
                          onClick={() => moveFieldDown(idx)}
                        >
                          <ArrowDownward fontSize="inherit" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}

          {/* Filters */}
          {selectedTable && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom sx={{ mr: 1 }}>
                  Filters
                </Typography>
                <IconButton onClick={() => handleOpenHelp("filters")} size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Box>
              {filters.map((filter, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}
                >
                  <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel>Field</InputLabel>
                    <Select
                      label="Field"
                      value={filter.field}
                      onChange={(e) => {
                        const updated = [...filters];
                        updated[idx].field = e.target.value;
                        setFilters(updated);
                      }}
                    >
                      <MenuItem value="">(None)</MenuItem>
                      {combinedColumnNames.map((colFull) => (
                        <MenuItem key={colFull} value={colFull}>
                          {colFull}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 90 }} size="small">
                    <InputLabel>Op</InputLabel>
                    <Select
                      label="Op"
                      value={filter.operator}
                      onChange={(e) => {
                        const updated = [...filters];
                        updated[idx].operator = e.target.value;
                        setFilters(updated);
                      }}
                    >
                      <MenuItem value="=">=</MenuItem>
                      <MenuItem value=">">&gt;</MenuItem>
                      <MenuItem value="<">&lt;</MenuItem>
                      <MenuItem value=">=">&gt;=</MenuItem>
                      <MenuItem value="<=">&lt;=</MenuItem>
                      <MenuItem value="!=">!=</MenuItem>
                      <MenuItem value="like">LIKE</MenuItem>
                      <MenuItem value="ilike">ILIKE</MenuItem>
                      <MenuItem value="between">BETWEEN</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Value"
                    size="small"
                    // This handles both strings and arrays
                    value={Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                    // This ensures the value is always stored as a string in the UI state
                    onChange={(e) => {
                      const updated = [...filters];
                      updated[idx].value = e.target.value;
                      setFilters(updated);
                    }}
                    sx={{ width: 200 }}
                    // Add a helper placeholder for the 'between' operator
                    placeholder={filter.operator === 'between' ? 'value1, value2' : ''}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeFilterRow(idx)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button onClick={addFilterRow}>+ Add Filter</Button>
            </Paper>
          )}

          {/* Group By */}
          {selectedTable && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom sx={{ mr: 1 }}>
                  Group By
                </Typography>
                <IconButton onClick={() => handleOpenHelp("groupBy")} size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                {fieldConfigs
                  .filter((fc) => fc.aggregator === "None")
                  .map((fc) => fc.column)
                  .map((col) => {
                    const isGB = groupBy.includes(col);
                    return (
                      <Box key={col} sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          size="small"
                          checked={isGB}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGroupBy([...groupBy, col]);
                            } else {
                              setGroupBy(groupBy.filter((g) => g !== col));
                            }
                          }}
                        />
                        <Typography>{col}</Typography>
                      </Box>
                    );
                  })}
              </Box>
            </Paper>
          )}

          {/* Sort */}
          {selectedTable && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom sx={{ mr: 1 }}>
                  Sort
                </Typography>
                <IconButton onClick={() => handleOpenHelp("sort")} size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {sort.map((s, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Field</InputLabel>
                      <Select
                        label="Field"
                        value={s.field}
                        onChange={(e) => {
                          const updated = [...sort];
                          updated[idx].field = e.target.value;
                          setSort(updated);
                        }}
                      >
                        {fieldConfigs.map((fc) => {
                          const name =
                            fc.aggregator === "None"
                              ? fc.column
                              : `${fc.aggregator}(${fc.column})`;
                          return (
                            <MenuItem key={name} value={name}>
                              {name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel>Dir</InputLabel>
                      <Select
                        label="Dir"
                        value={s.direction}
                        onChange={(e) => {
                          const updated = [...sort];
                          updated[idx].direction = e.target.value;
                          setSort(updated);
                        }}
                      >
                        <MenuItem value="ASC">ASC</MenuItem>
                        <MenuItem value="DESC">DESC</MenuItem>
                      </Select>
                    </FormControl>

                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        const updated = [...sort];
                        updated.splice(idx, 1);
                        setSort(updated);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Button
                variant="outlined"
                onClick={() => {
                  if (fieldConfigs.length > 0) {
                    const fc = fieldConfigs[0];
                    const name =
                      fc.aggregator === "None"
                        ? fc.column
                        : `${fc.aggregator}(${fc.column})`;
                    setSort([...sort, { field: name, direction: "ASC" }]);
                  }
                }}
                sx={{ mt: 1 }}
              >
                + Add Sort
              </Button>
            </Paper>
          )}

          {/* Actions */}
          {selectedTable && (
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
              <Button variant="contained" onClick={handleRunReport}>
                Run Report
              </Button>
              <IconButton onClick={() => handleOpenHelp("runReport")}>
                <HelpIcon fontSize="small" />
              </IconButton>

              <Button variant="outlined" onClick={handleExportCsv}>
                Export CSV
              </Button>
              <IconButton onClick={() => handleOpenHelp("exportCsv")}>
                <HelpIcon fontSize="small" />
              </IconButton>

              <Button variant="text" onClick={handleOpenSaveDialog}>
                Save Report
              </Button>
              <IconButton onClick={() => handleOpenHelp("saveReport")}>
                <HelpIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* Results */}
          {resultRows.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                Results ({resultRows.length} rows)
              </Typography>
              <Card sx={{ p: 2, maxHeight: 400, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {resultColumns.map((col) => (
                        <th
                          key={col}
                          style={{
                            border: "1px solid #ccc",
                            padding: "8px",
                            background: "#f0f0f0",
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resultRows.map((row, i) => (
                      <tr key={i}>
                        {resultColumns.map((c) => (
                          <td
                            key={c}
                            style={{ border: "1px solid #ccc", padding: "8px" }}
                          >
                            {String(row[c] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </Box>
          )}

          {/* Optional Chart */}
          {showChart && chartData && (
            <Box sx={{ height: 400, mb: 2 }}>
              {chartType === "bar" && <Bar data={chartData} />}
              {chartType === "doughnut" && <Doughnut data={chartData} />}
            </Box>
          )}
        </Box>

        {/* Delete Saved Report Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Saved Report?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "
              {reportToDelete ? reportToDelete.report_name : ""}"?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmDeleteSavedReport}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Report Dialog (Create New) */}
        <Dialog open={saveDialogOpen} onClose={handleCloseSaveDialog}>
          <DialogTitle>Save Report</DialogTitle>
          <DialogContent>
            <TextField
              label="Report Name"
              fullWidth
              sx={{ mt: 2 }}
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
            />
            <TextField
              label="Category"
              fullWidth
              sx={{ mt: 2 }}
              value={reportCategory}
              onChange={(e) => setReportCategory(e.target.value)}
            />
            <TextField
              label="Created By"
              fullWidth
              sx={{ mt: 2 }}
              value={reportCreatedBy}
              onChange={(e) => setReportCreatedBy(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSaveDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveReport}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Report Dialog (Update Existing) */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
          <DialogTitle>Edit Report</DialogTitle>
          <DialogContent>
            <TextField
              label="Report Name"
              fullWidth
              sx={{ mt: 2 }}
              value={reportNameEdit}
              onChange={(e) => setReportNameEdit(e.target.value)}
            />
            <TextField
              label="Category"
              fullWidth
              sx={{ mt: 2 }}
              value={reportCategoryEdit}
              onChange={(e) => setReportCategoryEdit(e.target.value)}
            />
            <TextField
              label="Created By"
              fullWidth
              sx={{ mt: 2 }}
              value={reportCreatedByEdit}
              onChange={(e) => setReportCreatedByEdit(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateSavedReport}>
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Help Dialog */}
        <Dialog open={Boolean(helpTopic)} onClose={handleCloseHelp} maxWidth="sm" fullWidth>
          <DialogTitle>Help</DialogTitle>
          <DialogContent>
            <DialogContentText whiteSpace="pre-line">
              {helpTopic && helpTexts[helpTopic]}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseHelp}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default ReportsModule;